import Button from 'components/Button';
import HoldingClassRightsForm from 'routes/LpPages/Common/HoldingClass/HoldingClassRightsForm';
import { useFormContext } from 'react-hook-form';
import ClassHoldingsList from './ClassHoldingsList';
import { Box, Grid, Typography, styled } from '@mui/material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import ErrorBoundary from 'components/ErrorBoundary';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { useHoldingClassDrawer } from './useHoldingClassDrawer';

type Props = {
  vehicleId: string;
  companyId: string;
  vehicleCurrencyCode: CurrencyCode;
};

const CenteredFormContainer = styled(Box)({
  margin: 'auto',
  width: '100%',
  maxWidth: 400,
});

const HoldingsOverview: React.FC<Props> = ({
  vehicleId,
  companyId,
  vehicleCurrencyCode,
}: Props) => {
  const [canWriteHoldingClasses] = useHasPermissions([
    RolePermission.LpWriteHoldingClasses,
  ]);

  const { activeDrawer, openDrawer, closeDrawer } = useHoldingClassDrawer();
  const { watch, getValues, setValue, clearErrors, trigger, control } =
    useFormContext();

  const holdingClasses = watch('holdingClasses');

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: createHoldingClass, isPending: isCreatingHoldingClass } =
    api.holdingClass.create.useMutation(
      mutationOpts({
        successMessage: 'Holding class created successfully.',
      }),
    );

  const { mutateAsync: deleteHoldingClass } =
    api.holdingClass.remove.useMutation(
      mutationOpts({
        successMessage: 'Holding class deleted successfully.',
      }),
    );

  const { mutateAsync: updateHoldingClass, isPending: isUpdatingHoldingClass } =
    api.holdingClass.update.useMutation(
      mutationOpts({
        successMessage: 'Holding class updated successfully.',
      }),
    );

  const handleAddHoldingClass = () => {
    if (!canWriteHoldingClasses) return;
    setValue('newHoldingClasses', [
      {
        name: '',
        assetId: undefined,
        currency: getValues('baseCurrency'),
        monitoringRights: undefined,
        liquidationPreferenceType: undefined,
        liquidationPreferenceMultiple: undefined,
        antiDilutionRights: undefined,
        couponRights: undefined,
        couponAmount: undefined,
      },
    ]);
    openDrawer({ type: 'addHoldingClass' });
  };

  const handleCancel = () => {
    setValue('newHoldingClasses', []);
    clearErrors('newHoldingClasses');
    closeDrawer();
  };

  const onSaveHoldingClass = async () => {
    if (!canWriteHoldingClasses) return;
    try {
      const holdingClass = getValues('newHoldingClasses');

      const firstHoldingClass = holdingClass[0];

      const isValid = await trigger(['newHoldingClasses.0.name']);
      if (!isValid) return;

      const newHoldingClasses = await createHoldingClass({
        assetId: companyId,
        vehicleId,
        holdingClass: firstHoldingClass,
      });

      if (newHoldingClasses) {
        setValue('holdingClasses', [newHoldingClasses, ...holdingClasses]);

        clearErrors('newHoldingClasses');

        setValue('newHoldingClasses', [
          {
            name: '',
            assetId: undefined,
            currency: getValues('baseCurrency'),
            monitoringRights: undefined,
            liquidationPreferenceType: undefined,
            liquidationPreferenceMultiple: undefined,
            antiDilutionRights: undefined,
            couponRights: undefined,
            couponAmount: undefined,
          },
        ]);

        closeDrawer();
      }
    } catch (error) {
      console.error('Failed to save holding class:', error);
    }
  };

  const onUpdateHoldingClass = async (
    holdingClassId?: string,
  ): Promise<boolean> => {
    if (!canWriteHoldingClasses) return false;
    try {
      if (!holdingClassId) {
        throw new Error('Holding class ID is required.');
      }

      const holdingClasses = getValues('holdingClasses');

      const holdingClass = holdingClasses.find(
        (hc) => hc._id === holdingClassId,
      );

      if (!holdingClass) {
        throw new Error('Holding class not found.');
      }

      const updatedHoldingClass = await updateHoldingClass({
        vehicleId,
        holdingClass,
      });

      if (updatedHoldingClass) {
        setValue(
          'holdingClasses',
          holdingClasses.map((hc) =>
            hc._id === holdingClassId ? updatedHoldingClass : hc,
          ),
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update holding class:', error);
      return false;
    }
  };

  const onDeleteHoldingClass = async (holdingClassId: string) => {
    if (!canWriteHoldingClasses) return;
    try {
      const deletedHoldingClassId = await deleteHoldingClass({
        id: holdingClassId,
        assetId: companyId,
        vehicleId,
      });

      if (deletedHoldingClassId) {
        setValue(
          'holdingClasses',
          holdingClasses.filter((hc) => hc._id !== deletedHoldingClassId),
        );
      }
    } catch (error) {
      console.error('Failed to delete holding class:', error);
    }
  };

  return (
    <>
      <Column spacing="sm">
        <Row fullWidth justify="space-between">
          <Typography variant="h5">Holdings</Typography>
          {canWriteHoldingClasses && (
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleAddHoldingClass}
            >
              Add holding class
            </Button>
          )}
        </Row>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <ErrorBoundary>
              <ClassHoldingsList
                holdingClasses={holdingClasses}
                onDeleteHoldingClass={onDeleteHoldingClass}
                onUpdateHoldingClass={onUpdateHoldingClass}
                isUpdatingHoldingClass={isUpdatingHoldingClass}
                companyId={companyId}
                vehicleId={vehicleId}
                vehicleCurrencyCode={vehicleCurrencyCode}
                editingHoldingClassId={
                  canWriteHoldingClasses &&
                  activeDrawer?.type === 'editHoldingClass'
                    ? activeDrawer.holdingClassId
                    : undefined
                }
                onEditHoldingClass={(id) => {
                  if (!canWriteHoldingClasses) return;
                  openDrawer({ type: 'editHoldingClass', holdingClassId: id });
                }}
                onCloseEditDrawer={closeDrawer}
              />
            </ErrorBoundary>
          </Grid>
        </Grid>
      </Column>
      <FullPageDrawer
        open={
          canWriteHoldingClasses && activeDrawer?.type === 'addHoldingClass'
        }
        onClose={handleCancel}
        title="Add holding class"
        size="sm"
        innerScroll
        actions={
          <Row justify="space-between">
            <Button variant="outlined" size="large" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onSaveHoldingClass}
              loading={isCreatingHoldingClass}
            >
              Add holding class
            </Button>
          </Row>
        }
      >
        <CenteredFormContainer>
          <HoldingClassRightsForm
            expandable
            index={0}
            formName="newHoldingClasses"
            control={control}
            vehicleCurrencyCode={vehicleCurrencyCode}
          />
        </CenteredFormContainer>
      </FullPageDrawer>
    </>
  );
};

export default HoldingsOverview;
